import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { CardNodeView } from './card-node-view'
import { nanoid } from 'nanoid'
import { IconCategory, IconType } from '@deepguide-ai/types'

export interface CardAttributes {
  title?: string
  icon?: string
  iconType?: IconType
  iconCategory?: IconCategory
  iconColor?: string
  showIcon?: boolean
  href?: string
  imagePath?: string
  callToAction?: string
}

export const CardSpec = {
  group: 'block',
  content: 'block*',
  attrs: {
    id: { default: nanoid(10) },
    title: { default: null },
    showIcon: { default: false },
    icon: { default: null },
    iconType: { default: null },
    iconCategory: { default: null },
    iconColor: { default: null },
  },
  toDOM(node: any): [string, Record<string, any>, 0] {
    const attrs: Record<string, any> = { 'data-card': '' };
    if (node.attrs.id) attrs.id = node.attrs.id;
    if (node.attrs.title) attrs.title = node.attrs.title;
    if (node.attrs.showIcon) attrs.showIcon = 'true';
    if (node.attrs.icon) attrs.icon = node.attrs.icon;
    if (node.attrs.iconType) attrs.iconType = node.attrs.iconType;
    if (node.attrs.iconCategory) attrs.iconCategory = node.attrs.iconCategory;
    if (node.attrs.iconColor) attrs.iconColor = node.attrs.iconColor;
    return ['div', attrs, 0];
  },
  parseDOM: [{
    tag: 'div[data-card]',
    getAttrs(dom: Element) {
      if (!(dom instanceof HTMLElement)) return {};
      return {
        id: dom.getAttribute('id'),
        title: dom.getAttribute('title'),
        showIcon: dom.getAttribute('showIcon') === 'true',
        icon: dom.getAttribute('icon'),
        iconType: dom.getAttribute('iconType'),
        iconCategory: dom.getAttribute('iconCategory'),
        iconColor: dom.getAttribute('iconColor'),
      };
    }
  }]
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    card: {
      /**
       * Insert a card
       */
      insertCard: (attributes?: CardAttributes) => ReturnType
      /**
       * Update card attributes
       */
      updateCard: (attributes: Partial<CardAttributes>) => ReturnType
    }
  }
}

export const CardExtension = Node.create({
  name: 'Card',

  group: 'block',

  content: 'block+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      title: {
        default: 'Card Title',
        parseHTML: (element) => element.getAttribute('data-title'),
        renderHTML: (attributes) => {
          if (!attributes.title) {
            return {}
          }
          return {
            'data-title': attributes.title,
          }
        },
      },
      icon: {
        default: 'sparkles',
        parseHTML: (element) => element.getAttribute('data-icon'),
        renderHTML: (attributes) => {
          if (!attributes.icon) {
            return {}
          }
          return {
            'data-icon': attributes.icon,
          }
        },
      },
      iconType: {
        default: 'REGULAR',
        parseHTML: (element) => element.getAttribute('data-icon-type'),
        renderHTML: (attributes) => {
          if (!attributes.iconType) {
            return {}
          }
          return {
            'data-icon-type': attributes.iconType,
          }
        },
      },
      iconCategory: {
        default: 'LUCIDE',
        parseHTML: (element) => element.getAttribute('data-icon-category'),
        renderHTML: (attributes) => {
          if (!attributes.iconCategory) {
            return {}
          }
          return {
            'data-icon-category': attributes.iconCategory,
          }
        },
      },
      iconColor: {
        default: '#3b82f6',
        parseHTML: (element) => element.getAttribute('data-icon-color'),
        renderHTML: (attributes) => {
          if (!attributes.iconColor) {
            return {}
          }
          return {
            'data-icon-color': attributes.iconColor,
          }
        },
      },
      showIcon: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-show-icon') === 'true',
        renderHTML: (attributes) => {
          if (attributes.showIcon === undefined) {
            return {}
          }
          return {
            'data-show-icon': attributes.showIcon,
          }
        },
      },
      href: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-href'),
        renderHTML: (attributes) => {
          if (!attributes.href) {
            return {}
          }
          return {
            'data-href': attributes.href,
          }
        },
      },
      imagePath: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-image-path'),
        renderHTML: (attributes) => {
          if (!attributes.imagePath) {
            return {}
          }
          return {
            'data-image-path': attributes.imagePath,
          }
        },
      },
      callToAction: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-call-to-action'),
        renderHTML: (attributes) => {
          if (!attributes.callToAction) {
            return {}
          }
          return {
            'data-call-to-action': attributes.callToAction,
          }
        },
      },
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-dg-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="Card"]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'Card' }), 0]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CardNodeView)
  },

  addCommands() {
    return {
      insertCard:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              title: 'Card Title',
              icon: 'sparkles',
              iconType: 'REGULAR',
              iconCategory: 'LUCIDE',
              iconColor: '#3b82f6',
              showIcon: false,
              href: '',
              imagePath: '',
              callToAction: '',
              ...attributes,
            },
            content: [
              {
                type: 'paragraph',
                content: [
                  
                ]
              }
            ],
          })
        },
      updateCard:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the card with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of a card
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
})
